import { Injectable } from '@angular/core';
import { FirebaseAuthService } from './firebase-auth.service';
import { Observable } from 'rxjs';
import { VoterService } from './voter.service';
import {environment} from "../../../environments/environment";

@Injectable({ providedIn: 'root' })
export class AuthService {
    // Store mapped permissions in memory
    private userPermissions: string[] = [];

    constructor(
        private firebaseAuth: FirebaseAuthService,
        private voterService: VoterService
    ) {}

    /**
     * Check if user is logged in
     * Requires both Firebase authentication and Calmean token
     */
    isLoggedIn(): boolean {
        return !!localStorage.getItem('firebaseUser') && !!localStorage.getItem('calmeanToken');
    }

    /**
     * Check if user is logged in with Firebase only
     */
    isFirebaseLoggedIn(): boolean {
        return !!localStorage.getItem('firebaseUser');
    }

    /**
     * Login with Google
     * This will authenticate with both Firebase and Calmean
     */
    loginWithGoogle(): Observable<any> {
        return this.firebaseAuth.signInWithGoogle();
    }

    /**
     * Logout from both Firebase and Calmean
     * Also clears cached permissions
     */
    logout(): void {
        // Clear cached permissions
        this.clearPermissions();

        this.firebaseAuth.logout().subscribe(() => {
            // Additional cleanup if needed
        });
    }

    /**
     * Clear cached permissions
     * Should be called when user logs out or when permissions change
     */
    clearPermissions(): void {
        console.log('Clearing cached permissions');
        this.userPermissions = [];
    }

    /**
     * Get the current user with combined Firebase and Calmean data
     */
    getCurrentUser() {
        const firebaseUser = JSON.parse(localStorage.getItem('firebaseUser') || 'null');

        // If we have a Calmean token but it's not in the user object, add a flag
        if (firebaseUser && localStorage.getItem('calmeanToken') && !firebaseUser.calmeanAccount) {
            return {
                ...firebaseUser,
                hasCalmeanAuth: true
            };
        }

        return firebaseUser;
    }

    tokenToWeb(accessCode: string) {

        console.log("accessCode: ", accessCode);

        var customUrl = environment.production ? "panel.calmean.com/" : "stage.calmean.com/";
        window.open((environment.production  ? "https://" : "http://") + customUrl + "login?accessCode=" + encodeURIComponent(accessCode));
    }

    loginWithAccessCode(accessCode: string, deviceId: string) {
        var customUrl = environment.production  ? "panel.calmean.com/" : "stage.calmean.com/";
        window.open((environment.production  ? "https://" : "http://") + customUrl + "login?accessCode=" + encodeURIComponent(accessCode) +
            "&accessCodeDevice=" + encodeURIComponent(deviceId) +
            "&accessCodeRole=BOK");
    }

    loginWithAccessCodeLocal(accessCode: string, deviceId: string) {
        window.open("http://localhost:4200/login?accessCode=" + encodeURIComponent(accessCode) +
            "&accessCodeDevice=" + encodeURIComponent(deviceId) +
            "&accessCodeRole=BOK");
    }

    loginWithAccessCodeBeta(accessCode: string, deviceId: string) {
        window.open("https://beta.calmean.com/login?accessCode=" + encodeURIComponent(accessCode) +
            "&accessCodeDevice=" + encodeURIComponent(deviceId) +
            "&accessCodeRole=BOK");
    }

    /**
     * Get the Calmean token if available
     */
    getCalmeanToken(): string | null {
        return localStorage.getItem('calmeanToken');
    }

    /**
     * Get the Account token if available
     */
    getAccountToken(): string | null {
        return localStorage.getItem('accountToken');
    }

    /**
     * Get all permissions for the current user
     * Maps roles to permissions using VoterService
     * @returns string[] Array of permissions
     */
    getUserPermissions(): string[] {
        // If we already have permissions in memory, return them
        if (this.userPermissions.length > 0) {
            return this.userPermissions;
        }

        const user = this.getCurrentUser();
        if (!user) {
            console.log('No user found');
            return [];
        }

        // Try to get calmeanAccount from user object
        let calmeanAccount = user.calmeanAccount;

        // If not found in user object, try to get from localStorage
        if (!calmeanAccount) {
            console.log('No calmeanAccount found in user, trying localStorage');
            const calmeanAccountJson = localStorage.getItem('calmeanAccount');
            if (calmeanAccountJson) {
                try {
                    calmeanAccount = JSON.parse(calmeanAccountJson);
                    console.log('Found calmeanAccount in localStorage:', calmeanAccount);
                } catch (e) {
                    console.error('Error parsing calmeanAccount from localStorage:', e);
                    return [];
                }
            } else {
                console.log('No calmeanAccount found in localStorage');
                return [];
            }
        }

        if (!calmeanAccount.rights || !Array.isArray(calmeanAccount.rights)) {
            console.log('No valid rights array found in calmeanAccount');
            return [];
        }

        console.log('User roles:', calmeanAccount.rights);

        // Map roles to permissions using VoterService
        this.userPermissions = this.voterService.mapRolesToPermissions(calmeanAccount.rights);

        // For backward compatibility, also include the original roles as permissions
        this.userPermissions = [...this.userPermissions, ...calmeanAccount.rights];

        // Remove duplicates
        this.userPermissions = [...new Set(this.userPermissions)];

        console.log('Mapped permissions:', this.userPermissions);

        return this.userPermissions;
    }

    /**
     * Check if the current user has a specific permission
     * @param permission The permission to check for
     * @returns boolean True if the user has the permission, false otherwise
     */
    hasPermission(permission: string): boolean {
        console.log('Checking permission:', permission);

        // Get all user permissions (mapped from roles)
        const permissions = this.getUserPermissions();

        if (permissions.length === 0) {
            console.log('No permissions found');
            return false;
        }

        // Check if the user has the specific permission
        const hasPermission = permissions.includes(permission);
        console.log('Has permission', permission, ':', hasPermission);

        return hasPermission;
    }
}
